import React, { useEffect, useState, useRef } from 'react';
import classes from '../../assets/scss/common.module.css';
import { Icons, Images } from '../../assets';
import { useLocation, useNavigate } from 'react-router-dom';
import ConfirmationModal from '../../modalComponents/ConfirmationModal';
import {
  getAllowedModulesByPillar,
  getResearchDataPermissions,
  logout,
} from '../../service/helpers';
import {
  CC,
  SUPER_ADMIN,
  ModuleKey,
  permissionKey,
  RESEARCH,
  researchPermissionConfig,
} from '../../constants/constants';
import { superAdminMicroApps } from '../../constants/staticData';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import {
  getLookUpApi,
  setCollectionData,
  setLocationData,
  setOrganisationData,
  setCurrentPillar,
  setCurrentModule,
  currentPillar,
} from '../../redux/reducers/adminSlice';
import { setLookupData } from '../../redux/reducers/lookupSlice';
import {
  getCollectioncenter,
  getLocationList,
  getOrganisation,
  getTrainingList,
} from '../../redux/reducers/diffusionSlice';
import { Toast } from '../../service/toast';
import { CCgetTrainingList } from '../../redux/reducers/ccSlice';
import usePermission from '../../utils/customeHooks/usePermission';
import {
  getResearchTrainingList,
  setDataSection,
  setFromResearch,
  setFromResearchTable,
  setResearchParticipantId,
} from '../../redux/reducers/researchSlice';

// ---------- module-level pure helpers ----------

function filterChildItems(section, allowedModules, researchDataPermissions) {
  const { pillarKey, list } = section;
  if (!list) return [];

  return list
    .map((child) => {
      const rawValue = child.value.includes('/') ? child.value.split('/')[1] : child.value;
      const backendKey = ModuleKey[rawValue] || rawValue;

      if (
        pillarKey === 'research' &&
        child.list?.length > 0 &&
        researchPermissionConfig[backendKey]
      ) {
        const { key, map } = researchPermissionConfig[backendKey];
        const allowedKeys = researchDataPermissions?.[key] || [];
        const filteredNested = child.list.filter((nested) =>
          allowedKeys.includes(map[nested.value])
        );
        return filteredNested.length > 0 ? { ...child, list: filteredNested } : null;
      }

      const isAllowed = allowedModules.includes(backendKey);
      return isAllowed ? child : null;
    })
    .filter(Boolean);
}

function buildFlatListFromApps(apps) {
  const flatList = [];
  apps.forEach((item) => {
    if (item?.list?.length) {
      item.list.forEach((child) => {
        if (child.list && child.list.length > 0) {
          flatList.push(...child.list);
        } else if (child.label && child.value) {
          flatList.push(child);
        }
      });
    } else {
      flatList.push(item);
    }
  });
  return flatList;
}

function resolvePathInfo(pathname) {
  const pathSegments = pathname.split('/');
  const currentPath = pathSegments[1];
  // tv/ and cc/ routes also use prefixed sidebar values (e.g. 'tv/approval', 'cc/dashboard')
  const isNestedPillarPath =
    currentPath === 'livelihood' ||
    currentPath === 'research' ||
    currentPath === 'tv' ||
    currentPath === 'cc';
  const actualPath = isNestedPillarPath ? pathSegments[2] : currentPath;
  let matchValue;
  if (currentPath === 'setting' && pathSegments[2]) {
    matchValue = `setting/${pathSegments[2]}`;
  } else {
    matchValue = isNestedPillarPath ? `${currentPath}/${actualPath}` : actualPath;
  }
  return { pathSegments, currentPath, actualPath, matchValue };
}

function findParentSection(microAppList, matchValue) {
  return microAppList.find(
    (section) =>
      Array.isArray(section.list) && section.list.some((child) => child.value === matchValue)
  );
}

function transformLookupData(rawData) {
  const result = {};
  for (const key in rawData) {
    const value = rawData[key];
    if (!Array.isArray(value)) {
      result[key] = value;
      continue;
    }
    const seen = new Set();
    result[key] = value
      .filter((item) => {
        const val = item.beValue?.toLowerCase();
        if (!val || seen.has(val)) return false;
        seen.add(val);
        return true;
      })
      .map((item) => ({
        label: item.name,
        value: item.beValue,
        ...Object.fromEntries(
          Object.entries(item).filter(([k]) => k !== 'name' && k !== 'beValue')
        ),
      }));
  }
  return result;
}

function computeIsPillarActive(data, activePillar, activeMicroApp) {
  if (activePillar === data.value) return true;
  return (
    data.list?.some(
      (child) =>
        child.value === activeMicroApp ||
        child.list?.some((nested) => nested.value === activeMicroApp)
    ) ?? false
  );
}

function checkHasValidChildren(list) {
  return Array.isArray(list) && list.some((child) => child.label && child.value);
}

// ---------- component ----------

const Sidebar = () => {
  const [microAppList, setMicroAppList] = useState([]);
  const [flatAppList, setFlatAppList] = useState([]);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [activeMicroApp, setActiveMicroApp] = useState('dashboard');
  const [activePillar, setActivePillar] = useState(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isNestedList, setIsNestedList] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [expandedSection, setExpandedSection] = useState(null);
  const [expandedNestedItem, setExpandedNestedItem] = useState(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const listRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const userRoles = useSelector((state) => state?.auth?.userRole);
  const userPillar = useSelector(currentPillar);
  const expandedMicroApp = localStorage?.getItem('expandedSection');
  const accessPermissions = useSelector((state) => state?.auth?.permissions || []);
  const { getModulePermission } = usePermission();
  const hasSettingsPermission = getModulePermission('common', 'settings', permissionKey.view);
  const dropdownRefs = useRef({});
  const [isChildScrolling, setIsChildScrolling] = useState(false);
  const [childScrollPosition, setChildScrollPosition] = useState(0);
  const [childCanScrollDown, setChildCanScrollDown] = useState(false);
  const [showNavGuard, setShowNavGuard] = useState(false);
  const pendingNavTarget = useRef(null);
  const isFormDirty = useSelector((state) => state?.livelihood?.isFormDirty);

  const isSinglePillarView = isNestedList && microAppList.length === 1;

  useEffect(() => {
    const handleResizeAndCollapse = () => {
      const mobile = window.innerWidth <= 800;
      setIsMobile(mobile);
      if (mobile) {
        setIsSidebarCollapsed(true);
      }
    };

    handleResizeAndCollapse();
    window.addEventListener('resize', handleResizeAndCollapse);

    return () => window.removeEventListener('resize', handleResizeAndCollapse);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setIsSidebarCollapsed(true);
    }
  }, [location.pathname, isMobile]);

  useEffect(() => {
    const researchDataPermissions = getResearchDataPermissions(accessPermissions);

    let apps = [...superAdminMicroApps].map((section) => {
      const allowedModules = getAllowedModulesByPillar(accessPermissions, section?.pillarKey);
      if (!section.list?.length) return section;
      const filteredChildren = filterChildItems(section, allowedModules, researchDataPermissions);
      return { ...section, list: filteredChildren };
    });

    apps = apps.filter((section) => (Array.isArray(section.list) ? section.list.length > 0 : true));

    setIsNestedList(true);
    setMicroAppList(apps);
    setFlatAppList(buildFlatListFromApps(apps));
  }, [userPillar, userRoles, accessPermissions]);

  const updateActiveState = (matchValue, match, isStandalone) => {
    setActiveMicroApp(matchValue);

    if (isStandalone) {
      setActivePillar(null);
      return;
    }

    if (isNestedList && match) {
      const parent = findParentSection(microAppList, matchValue);
      const pillarToSet = expandedMicroApp || parent?.value;
      if (pillarToSet) {
        setExpandedSection(pillarToSet);
        setActivePillar(pillarToSet);
      }
    }
  };

  useEffect(() => {
    if (!location?.pathname || flatAppList.length === 0) return;
    const { pathSegments, currentPath, actualPath, matchValue } = resolvePathInfo(
      location.pathname
    );
    const match = flatAppList.find((item) => item.value === matchValue && !item?.list?.length);
    const isStandalone =
      matchValue === 'usermanagement' || (currentPath === 'setting' && pathSegments[2]);
    if ((match && actualPath !== 'logout') || isStandalone) {
      updateActiveState(matchValue, match, isStandalone);
    }
  }, [location, flatAppList, microAppList, expandedMicroApp, userPillar]);

  useEffect(() => {
    if (!expandedSection) {
      setIsChildScrolling(false);
      setChildScrollPosition(0);
      setChildCanScrollDown(false);
      return;
    }

    const dropdown = dropdownRefs.current[expandedSection];
    if (!dropdown) return;

    const updateScrollState = () => {
      const { scrollTop, scrollHeight, clientHeight } = dropdown;
      setChildScrollPosition(scrollTop);
      setIsChildScrolling(scrollHeight > clientHeight);
      setChildCanScrollDown(scrollTop + clientHeight < scrollHeight - 1);
    };

    const timeout = setTimeout(() => {
      updateScrollState();
    }, 350);

    dropdown.addEventListener('scroll', updateScrollState);

    return () => {
      clearTimeout(timeout);
      dropdown.removeEventListener('scroll', updateScrollState);
    };
  }, [expandedSection, microAppList]);

  const getLookupDetails = async () => {
    const transformedData = {};
    await getLookUpApi({ type: 'ALL' })
      .then((res) => {
        Object.assign(transformedData, transformLookupData(res?.data));
      })
      .catch((err) => {
        console.error('Error fetching lookup data:', err);
      });

    let requestApi = getTrainingList;
    if (userPillar?.toLowerCase() === CC) requestApi = CCgetTrainingList;
    else if (userPillar?.toLowerCase() === RESEARCH) requestApi = getResearchTrainingList;

    let trainingOptions = [];
    await requestApi({ order: 'DESC', take: 50 })
      .then((res) => {
        const rawData = res?.data?.data || [];
        trainingOptions = rawData.map((item) => ({
          label: item.moduleName,
          value: item.id,
        }));
      })
      .catch((err) => {
        Toast({
          type: 'error',
          message: err?.response?.data?.message || 'Unable to process the request!..',
        });
      });

    dispatch(
      setLookupData({
        ...transformedData,
        ...(trainingOptions && transformedData && { module: trainingOptions || [] }),
      })
    );
  };

  const getCollectionList = async () => {
    const requestData = { page: 1, take: 100 };
    let collectionList = [];
    await getCollectioncenter(requestData)
      .then((response) => {
        const rawData = response?.data?.data || [];
        collectionList = rawData.map((item) => ({
          label: item?.collectionCenterName,
          value: item?.id,
          collectionCenterID: item?.collectionCenterID,
        }));
      })
      .catch((error) => {
        console.error('Error fetching Collectioncenter:', error);
      });
    dispatch(setCollectionData(collectionList));
  };

  const getOrganisationList = async () => {
    const requestData = { page: 1, take: 50 };
    let organisationList = [];
    await getOrganisation(requestData)
      .then((response) => {
        const rawData = response?.data?.data || [];
        organisationList = rawData.map((item) => ({
          label: item.organisationName,
          value: item.organisationName,
          uuid: item.id,
        }));
      })
      .catch((error) => {
        console.error('Error fetching Organisation:', error);
      });
    dispatch(setOrganisationData(organisationList));
  };

  const getLocation = async () => {
    const paramsObj = { order: 'DESC', take: 50, page: 1 };
    let locationList = [];
    await getLocationList(paramsObj)
      .then((res) => {
        const rawData = res?.data?.data || [];
        locationList = rawData.map((item) => ({
          value: item.name,
          label: item.name,
        }));
      })
      .catch((err) => {
        Toast({
          type: 'error',
          message: err?.response?.data?.message || 'Unable to process the request!..',
        });
      });
    dispatch(setLocationData(locationList));
  };

  useEffect(() => {
    getLocation();
    getCollectionList();
    getOrganisationList();
    getLookupDetails();
  }, [userPillar]);

  useEffect(() => {
    if (listRef.current) {
      const { scrollHeight } = listRef.current;
      const { clientHeight } = listRef.current;
      setIsScrolling(scrollHeight > clientHeight);
    }
  }, [microAppList, expandedSection]);

  useEffect(() => {
    if (isSinglePillarView && microAppList?.[0]?.value) {
      setExpandedSection(microAppList[0].value);
    }
  }, [isSinglePillarView, microAppList]);

  const handleRedirect = (key, parentKey, pillaryKey) => {
    if (isFormDirty) {
      pendingNavTarget.current = { key, parentKey, pillaryKey };
      setShowNavGuard(true);
      return;
    }
    executeRedirect(key, parentKey, pillaryKey);
  };

  const executeRedirect = (key, parentKey, pillaryKey) => {
    setActiveMicroApp(key);
    setActivePillar(parentKey);
    if (parentKey) {
      localStorage?.setItem('expandedSection', parentKey);
    } else {
      localStorage?.removeItem('expandedSection');
    }

    const moduleName = key.includes('/') ? key.split('/')[1] : key;

    dispatch(setCurrentPillar(pillaryKey || null));
    dispatch(setCurrentModule(moduleName));

    dispatch(setDataSection(''));
    dispatch(setResearchParticipantId(''));
    dispatch(setFromResearch(false));
    dispatch(setFromResearchTable(false));

    navigate(`/${key}`);

    if (isMobile && key !== 'logout') {
      setIsSidebarCollapsed(true);
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
    if (isMobile) {
      setIsSidebarCollapsed(true);
    }
  };

  const checkScrolling = () => {
    if (listRef.current) {
      const { scrollHeight } = listRef.current;
      const { clientHeight } = listRef.current;
      setIsScrolling(scrollHeight > clientHeight);
    }
  };

  const handleSectionToggle = (sectionId) => {
    if (isSinglePillarView) return;
    setExpandedSection((prev) => {
      const newSection = prev === sectionId ? null : sectionId;
      if (prev === sectionId) {
        setIsChildScrolling(false);
        setChildScrollPosition(0);
        setExpandedNestedItem(null);
      }
      setTimeout(checkScrolling, 300);
      return newSection;
    });
  };

  const handleNestedToggle = (nestedId) => {
    setExpandedNestedItem((prev) => (prev === nestedId ? null : nestedId));
  };

  const handleClose = () => setShowLogoutModal(false);

  const logoutConfirmation = () => logout();

  const handleScrollDown = () => {
    if (expandedSection && dropdownRefs.current[expandedSection]) {
      const dropdown = dropdownRefs.current[expandedSection];
      dropdown.scrollTop += dropdown.clientHeight;
    } else if (listRef.current) {
      const { scrollHeight } = listRef.current;
      const currentHeight = listRef.current.offsetHeight;
      const newScrollPosition = Math.min(
        scrollPosition + currentHeight,
        scrollHeight - currentHeight
      );
      listRef.current.scrollTop = newScrollPosition;
      setScrollPosition(newScrollPosition);
    }
  };

  const handleScrollUp = () => {
    if (expandedSection && dropdownRefs.current[expandedSection]) {
      const dropdown = dropdownRefs.current[expandedSection];
      dropdown.scrollBy({ top: -dropdown.clientHeight, behavior: 'smooth' });
    } else if (listRef.current) {
      const currentHeight = listRef.current.offsetHeight;
      const newScrollPosition = Math.max(scrollPosition - currentHeight, 0);
      listRef.current.scrollTo({ top: newScrollPosition, behavior: 'smooth' });
      setScrollPosition(newScrollPosition);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // ---------- render helpers ----------

  const renderNestedGrandchild = (nestedChild, data) => (
    <div
      key={nestedChild.value}
      className={`${classes.microAppWrap} ${classes.nestedChild} ${
        activeMicroApp === nestedChild.value && (activePillar === data?.value || isSinglePillarView)
          ? classes.microAppActive
          : ''
      } flex items-center`}
      onClick={(e) => {
        e.stopPropagation();
        handleRedirect(nestedChild.value, data?.value, data?.pillarKey);
      }}
    >
      {nestedChild?.icon && (
        <div className={classes.microNestedLogo}>
          <img
            src={Icons?.[nestedChild.icon]}
            alt={nestedChild.label}
            className={classes.iconSmall}
          />
        </div>
      )}
      <span>{nestedChild.label}</span>
    </div>
  );

  const renderChildWithNestedList = (child, data) => {
    const isNestedOpen = expandedNestedItem === child.value;
    const isSectionActive =
      activeMicroApp === child.value || child.list?.some((c) => activeMicroApp.startsWith(c.value));
    return (
      <div key={child.value}>
        <div
          className={`${classes.microAppWrap} ${classes.nestedParent} flex items-center justify-between
            ${isSectionActive ? classes.microAppActive : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            handleNestedToggle(child.value);
          }}
        >
          <div className={`flex items-center gap-3 ${classes.microAppLogo}`}>
            {child?.icon && (
              <img src={Icons?.[child.icon]} alt={child.label} className={classes.icon} />
            )}
            <span>{child.label}</span>
          </div>
          <span className={classes.chevronIcon}>
            {isNestedOpen ? <ExpandLessIcon size={18} /> : <ExpandMoreIcon size={18} />}
          </span>
        </div>
        <div
          className={`${classes.nestedDropdown} ${isNestedOpen ? classes.open : ''}`}
          style={{
            maxHeight: isNestedOpen ? `${child.list.length * 50}px` : 0,
            transition: 'max-height 0.3s ease-in-out',
            overflow: 'hidden',
          }}
        >
          {child.list.map((nestedChild) => renderNestedGrandchild(nestedChild, data))}
        </div>
      </div>
    );
  };

  const renderSidebarChild = (child, data) => {
    if (child.list && child.list.length > 0) return renderChildWithNestedList(child, data);
    return (
      <div
        key={child.value}
        className={`${classes.microAppWrap} ${
          activeMicroApp === child.value && (activePillar === data?.value || isSinglePillarView)
            ? classes.microAppActive
            : ''
        } flex items-center`}
        onClick={() => handleRedirect(child.value, data?.value, data?.pillarKey)}
      >
        {child?.icon && (
          <div className={classes.microAppLogo}>
            <img src={Icons?.[child.icon]} alt={child.label} className={classes.icon} />
          </div>
        )}
        {child.label}
      </div>
    );
  };

  const renderPillarItem = (data) => {
    const hasValidChildren = checkHasValidChildren(data.list);
    if (!hasValidChildren) {
      return (
        <div
          key={data.value}
          className={`${classes.microAppWrap} ${activeMicroApp === data.value ? classes.microAppActive : ''} flex items-center gap-3`}
          onClick={() => handleRedirect(data.value)}
        >
          {data?.icon && (
            <div className={classes.microAppLogo}>
              <img
                src={Icons?.[data.icon]}
                alt={data.label}
                className='w-6 h-6 brightness-0 invert'
              />
            </div>
          )}
          <div className={classes.microAppLable}>{data.label}</div>
        </div>
      );
    }
    const isOpen = isSinglePillarView || expandedSection === data.value;
    const isPillarActive = computeIsPillarActive(data, activePillar, activeMicroApp);
    return (
      <div key={data.value}>
        <div
          className={`${classes.sectionHeader} ${isPillarActive ? classes.microAppActive : ''}`}
          onClick={() =>
            data?.list?.length > 0
              ? !isSinglePillarView && handleSectionToggle(data.value)
              : handleRedirect(data.value)
          }
        >
          {data.label}
          {!isSinglePillarView && (
            <span className={classes.chevronIcon}>
              {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </span>
          )}
        </div>
        <div
          ref={(el) => (dropdownRefs.current[data.value] = el)}
          className={`${classes.dropdownWrapper} ${isOpen ? classes.open : ''}`}
          style={{
            maxHeight: isOpen ? 'none' : 0,
            transition: 'max-height 0.3s',
            overflow: 'hidden',
          }}
        >
          {data?.list?.map((child) => renderSidebarChild(child, data))}
        </div>
      </div>
    );
  };

  const renderFooterItems = () => (
    <>
      <div className={classes.arrowWrapper}>
        <div
          className={`${classes.arrowDown} ${(isScrolling && scrollPosition + listRef.current?.clientHeight < listRef.current?.scrollHeight - 1) || (isChildScrolling && childCanScrollDown) ? classes.visible : classes.hidden}`}
          onClick={handleScrollDown}
        >
          <img src={Icons.chevronArrow} alt='Scroll Down' />
        </div>
      </div>

      {userRoles?.toLowerCase() === SUPER_ADMIN?.toLowerCase() && (
        <div
          className={`${classes.microAppWrap} ${activeMicroApp === 'usermanagement' ? classes.microAppActive : ''} flex items-center`}
          onClick={() => handleRedirect('usermanagement')}
        >
          <div className={classes.microAppLogo}>
            <img src={Icons.usermanagement} alt='User Management' />
          </div>
          <div className={classes.microAppLable}>User Management</div>
        </div>
      )}

      {hasSettingsPermission && (
        <div
          className={`${classes.microAppWrap} ${activeMicroApp === 'setting/profile' ? classes.microAppActive : ''} flex items-center`}
          onClick={() => handleRedirect('setting/profile')}
        >
          <div className={classes.microAppLogo}>
            <img src={Icons.setting} alt='Settings' />
          </div>
          <div className={classes.microAppLable}>Settings</div>
        </div>
      )}

      <div className={`${classes.microAppWrap} flex items-center`} onClick={handleLogoutClick}>
        <div className={classes.microAppLogo}>
          <img src={Icons.logout} alt='Logout' />
        </div>
        <div className={classes.microAppLable}>Log Out</div>
      </div>
    </>
  );

  return (
    <div>
      {isMobile && (
        <button
          className={classes.mobileToggle}
          onClick={toggleSidebar}
          aria-label='Toggle sidebar'
          style={{
            top: isSidebarCollapsed ? '15px' : '5px',
            left: isSidebarCollapsed ? '15px' : '250px',
          }}
        >
          {isSidebarCollapsed ? <MenuIcon /> : <CloseIcon />}
        </button>
      )}

      {isMobile && !isSidebarCollapsed && (
        <div className={classes.overlay} onClick={() => setIsSidebarCollapsed(true)} />
      )}

      <div className={`${classes.sidebarWrapper} ${isSidebarCollapsed ? classes.collapsed : ''}`}>
        <div className={classes.mainLogo}>
          <img src={Images.logoWhite} alt='logo' />
        </div>

        <div className={classes.sidebarContent}>
          <div className={classes.arrowWrapper}>
            <div
              className={`${classes.arrowUp} ${(isScrolling && scrollPosition > 0) || (isChildScrolling && childScrollPosition > 0) ? classes.visible : classes.hidden}`}
              onClick={handleScrollUp}
            >
              <img src={Icons.chevronArrow} alt='Scroll Up' />
            </div>
          </div>

          <div
            ref={listRef}
            className={classes.sidebarScroller}
            style={{ overflowY: 'auto' }}
            onScroll={(e) => setScrollPosition(e.target.scrollTop)}
          >
            {microAppList.map(renderPillarItem)}
          </div>

          {renderFooterItems()}
        </div>
      </div>

      {showLogoutModal && (
        <ConfirmationModal
          isOpen={showLogoutModal}
          closePopUp={handleClose}
          title='Confirm Logout'
          description={
            <>
              Are you sure you want to logout<span className='normal-QuestionMark'> ?</span>
            </>
          }
          nobtnText='Cancel'
          yesbtnText='Logout'
          clearHandler={handleClose}
          successHandler={logoutConfirmation}
        />
      )}

      {showNavGuard && (
        <ConfirmationModal
          isOpen={showNavGuard}
          closePopUp={() => setShowNavGuard(false)}
          title='Unsaved Changes'
          description={
            <div className='text-center'>
              You have unsaved changes. Are you sure you want to leave?
              <br />
              <span className='text-sm text-gray-500 font-normal'>
                Your changes will be lost if you navigate away.
              </span>
            </div>
          }
          nobtnText='Stay on Page'
          yesbtnText='Leave Anyway'
          clearHandler={() => setShowNavGuard(false)}
          successHandler={() => {
            setShowNavGuard(false);
            if (pendingNavTarget.current) {
              const { key, parentKey, pillaryKey } = pendingNavTarget.current;
              executeRedirect(key, parentKey, pillaryKey);
              pendingNavTarget.current = null;
            }
          }}
        />
      )}
    </div>
  );
};

export default Sidebar;
